import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import RenderColumnUtils from "go-list/core/components/Actions/services/RenderColumnUtils";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import {
	StocktakingApi,
	StocktakingDraftSummaryStateApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { StocktakingContext, StocktakingMobileActionsContext } from "../../../../Preview";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "STOCKTAKING_DRAFT_SUMMARY_LIST";
const resourceType = "STOCKTAKING_DRAFT_SUMMARY_LIST";

const buildConfig = (
	t: TFunction,
	data: Record<string, any>,
	segmentListService: SegmentListService,
	stocktaking: StocktakingApi,
	organization: OrganizationState
) => {
	let config = {
		fields: [
			{
				id: "product_category",
				name: t("common.word.category"),
			},
			{
				id: "product_name",
				name: t("common.word.product"),
				render: (item: StocktakingDraftSummaryStateApi) => (
					<Link to={`/${organization.id}/products/${item.product_id}/edit`} target="_blank" rel="noreferrer">
						{item.product_name}
					</Link>
				),
				renderExport: (item: StocktakingDraftSummaryStateApi) => item.product_name,
			},
			{
				id: "sku",
				name: t("common.word.sku"),
				type: "text",
			},
			{
				id: "volumes",
				name: t("modules.stocktaking_draft_summary.field.volumes.title"),
				disableSorting: true,
				render: (item: StocktakingDraftSummaryStateApi) => {
					const measureType = t(`enums.products.measure_types.${item.measure_type}`);
					return (
						<div className="d-flex flex-column">
							{item.items.map((item, index) => {
								const { stocktaking_draft_name, volume, amount } = item;
								return (
									<span key={`volumes-${new Date().getTime()}-${index}`}>
										{`${stocktaking_draft_name}: ${amount} x ${volume} ${measureType}`}
									</span>
								);
							})}
						</div>
					);
				},
				renderExport: (item: StocktakingDraftSummaryStateApi) => {
					const measureType = t(`enums.products.measure_types.${item.measure_type}`);
					return item.items
						.map((item) => {
							const { stocktaking_draft_name, volume, amount } = item;
							return `${stocktaking_draft_name}: ${amount} x ${volume} ${measureType}`;
						})
						.join(", ");
				},
			},
			{
				id: "amount",
				name: t("modules.stocktaking_draft_summary.field.sum.title"),
				render: (item: StocktakingDraftSummaryStateApi) => {
					const measureType = t(`enums.products.measure_types.${item.measure_type}`);
					return `${item.amount} ${measureType}`;
				},
			},
		],
		actions: [],
		filters: [
			{
				id: "productIds",
				name: t("common.word.product"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductSearchSelect(search, params, { cancelToken: options?.token }),
				},
			},
			{
				id: "categoryIds",
				name: t("common.word.category"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductCategorySearchSelect(search, params, { cancelToken: options?.token }),
				},
			},
		],
		selectedColumns: ["product_category", "product_name", "volumes", "amount"],
		customPageSizes: ["20", "50", "100", "1000"],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: `${t("modules.stocktaking_draft_summary.field.export_config.title")} ${stocktaking.document_number}`,
			filename: `${t("modules.stocktaking_draft_summary.field.export_filename.title")}_${FormatDate(
				stocktaking.stocktaking_at
			).replaceAll(".", "-")}`,
			organization: organization.name,
		},
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) =>
			apiOrganization.getStocktakingDraftSummary(stocktaking.id, params, {
				cancelToken: sourceToken?.token,
			}),
		saveSegment: (segment: SegmentType) => segmentListService.save(listName, resourceType, segment),
	} as ListConfig;

	const valuesField = {
		id: "values",
		name: t("modules.stocktaking_draft_summary.field.values.title"),
		disableSorting: true,
		render: (item: StocktakingDraftSummaryStateApi) => {
			return (
				<div className="d-flex flex-column">
					{item.items.map((item, index) => {
						const { stocktaking_draft_name, value } = item;
						return (
							<span key={`values-${new Date().getTime()}-${index}`}>
								{`${stocktaking_draft_name}: ${FormatMoney(value)}`}
							</span>
						);
					})}
				</div>
			);
		},
		renderExport: (item: StocktakingDraftSummaryStateApi) =>
			item.items
				.map((item) => {
					const { stocktaking_draft_name, value } = item;
					return `${stocktaking_draft_name}: ${FormatMoney(value)}`;
				})
				.join(", "),
	};

	const valueField = {
		id: "value",
		name: t("common.word.value"),
		render: (item: StocktakingDraftSummaryStateApi) => FormatMoney(item.value),
		styleOverride: RenderColumnUtils.getMoneyStyles(),
	};

	if (stocktaking.type === "VALUE") {
		config.fields = [...(config.fields || []), valuesField, valueField];
		config.selectedColumns = [...(config.selectedColumns || []), valuesField.id, valueField.id];
	}

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};

const List: FC<ListState> = ({ resource }) => {
	const { t } = useTranslation();
	const segmentContextValue = useContext(SegmentContext);
	const { stocktaking } = useContext(StocktakingContext);
	const mobileActions = useContext(StocktakingMobileActionsContext);
	const organization = useSelector(selectOrganization);

	if (!resource) return null;
	const data = resource.read();

	const config = buildConfig(t, data, segmentContextValue, stocktaking, organization);

	return <ListData config={config} withoutSegments mobileActions={mobileActions} />;
};

const StocktakingDraftSummaryList = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);

	useEffect(() => {
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="stocktaking-draft-state-list">
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</div>
	);
};

export default StocktakingDraftSummaryList;
